import { inject, Injectable } from '@angular/core';
import { getHostnameFromUrl } from '@shared/utils/track-outbound-link.util';
import { MatomoTracker } from 'ngx-matomo-client';

import type { AnalyticsDimensionId } from './analytics-dimensions';
import { ANALYTICS_DIMENSIONS } from './analytics-dimensions';

export interface AnalyticsEvent {
  category: string;
  action: string;
  name?: string;
  value?: number;
}

export type OutboundLinkSource = 'folder' | 'product' | 'news' | 'quick_link';

export type OutboundLinkAction = 'click' | 'video_open';

export interface OutboundLinkContext {
  source: OutboundLinkSource;
  section: string;
  title: string;
  url?: string | null;
  action?: OutboundLinkAction;
}

/**
 * AnalyticsService - Wraps Matomo tracking (privacy-aware, no PII)
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly tracker = inject(MatomoTracker, { optional: true });

  /**
   * Track page view
   */
  public trackPageView(): void {
    this.tracker?.trackPageView();
  }

  /**
   * Track a custom interaction event.
   */
  public trackEvent(event: AnalyticsEvent): void;

  public trackEvent(category: string, action: string, name?: string, value?: number): void;

  public trackEvent(categoryOrEvent: string | AnalyticsEvent, action?: string, name?: string, value?: number): void {
    if (!this.tracker) {
      return;
    }

    if (typeof categoryOrEvent === 'object') {
      const event = categoryOrEvent;
      this.tracker.trackEvent(event.category, event.action, event.name, event.value);
      return;
    }

    this.tracker.trackEvent(categoryOrEvent, action ?? '', name, value);
  }

  /**
   * Set visit-scoped custom dimensions from the authenticated user's permission groups.
   */
  public initializeUserContext(permissionGroupTitles: string): void {
    this.setCustomDimension(ANALYTICS_DIMENSIONS.permissionGroups, permissionGroupTitles);
  }

  /**
   * Clear visit-scoped user context.
   */
  public clearUserContext(): void {
    this.deleteCustomDimension(ANALYTICS_DIMENSIONS.permissionGroups);
  }

  /**
   * Set a visit-scoped custom dimension (Matomo Custom Dimensions plugin).
   */
  public setCustomDimension(dimensionId: AnalyticsDimensionId, value: string): void {
    if (!this.tracker || !value.trim()) {
      return;
    }
    this.tracker.setCustomDimension(dimensionId, value.trim());
  }

  /**
   * Clear a custom dimension for the current visit.
   */
  public deleteCustomDimension(dimensionId: AnalyticsDimensionId): void {
    this.tracker?.deleteCustomDimension(dimensionId);
  }

  /**
   * Track an internal site search keyword (Matomo Behaviour → Site Search).
   */
  public trackSiteSearch(keyword: string, resultsCount: number): void {
    const trimmed = keyword.trim();
    if (!this.tracker || !trimmed) {
      return;
    }
    this.tracker.trackSiteSearch(trimmed, 'global_search', resultsCount);
  }

  /**
   * Track outbound link clicks with business context.
   */
  public trackOutboundLink(context: OutboundLinkContext): void {
    const hostname = getHostnameFromUrl(context.url);
    const action = context.action ?? 'click';

    this.trackEvent({
      category: 'outbound_link',
      action,
      name: `${context.source}|${context.section}|${context.title}|${hostname ?? 'unknown'}`
    });
  }

  /**
   * Grant tracking consent and persist the choice (for future consent banner).
   */
  public grantTrackingConsent(hoursToExpire?: number): void {
    this.tracker?.rememberConsentGiven(hoursToExpire);
  }

  /**
   * Revoke tracking consent (for future consent banner).
   */
  public revokeTrackingConsent(): void {
    this.tracker?.forgetConsentGiven();
  }
}
